import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { HiOutlineUser, HiOutlineExclamationCircle } from 'react-icons/hi';
import { profileAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { getFriendlyError } from '../utils/errorMessage';
import LoadingAnimation from '../components/LoadingAnimation';
import WelcomeTutorial from '../components/WelcomeTutorial';
import EmptyStateDiscover from '../components/EmptyStateDiscover';
import ActivityBanner from '../components/ActivityBanner';
import './Discover.css';

const Discover = () => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all'); // all, nearby, new
  const [searchQuery, setSearchQuery] = useState('');
  const [ageFilter, setAgeFilter] = useState({ min: 18, max: 99 });
  const [genderFilter, setGenderFilter] = useState('all'); // all, MALE, FEMALE, OTHER
  const [distanceFilter, setDistanceFilter] = useState(500); // Default 500km
  // "Available for call" filter removed - video calls are WhatsApp-style from chat
  const [showFilters, setShowFilters] = useState(false);
  const [showProfileSetup, setShowProfileSetup] = useState(false);
  const [profileCheckComplete, setProfileCheckComplete] = useState(false);
  const [currentUserProfile, setCurrentUserProfile] = useState(null); // Store current user's profile
  const [showWelcomeTutorial, setShowWelcomeTutorial] = useState(false);
  const [tutorialUserId, setTutorialUserId] = useState(null); // Reliable user id for tutorial flag
  const { userId } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    checkProfileStatus();
  }, []);

  // Update activity every 30 seconds while on Discover page
  useEffect(() => {
    const interval = setInterval(() => {
      profileAPI.updateActivity().catch(err => 
        console.error('Failed to update activity:', err)
      );
    }, 30000); // Every 30 seconds

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!showProfileSetup) {
      loadProfiles();
    }
  }, [filter, showProfileSetup]);

  // Prevent navigation away if profile is incomplete
  useEffect(() => {
    if (showProfileSetup) {
      const handleBeforeUnload = (e) => {
        e.preventDefault();
        e.returnValue = '';
      };
      window.addEventListener('beforeunload', handleBeforeUnload);
      return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }
  }, [showProfileSetup]);

  const checkProfileStatus = async () => {
    try {
      const response = await profileAPI.getMyProfile();

      // Store current user's profile
      setCurrentUserProfile(response.data);

      // Resolve a reliable user id (auth context may not be hydrated on mount).
      // Prefer the id from the profile response so read/write keys always match.
      const resolvedUserId = response.data.userId
        || response.data.user?.id
        || userId
        || localStorage.getItem('userId');
      setTutorialUserId(resolvedUserId);
      
      // Check if this is a first-time user (show tutorial only once)
      const hasSeenTutorial = resolvedUserId
        ? localStorage.getItem(`hasSeenTutorial_${resolvedUserId}`)
        : true; // if we can't identify the user, don't show to avoid repeat prompts
      
      // Show tutorial only if user hasn't seen it before and has completed profile setup
      if (!hasSeenTutorial && response.data.gender) {
        setShowWelcomeTutorial(true);
      }
      
      // Only show setup modal if profile is actually missing gender or interestedIn
      const hasGender = response.data.gender !== null && response.data.gender !== undefined;
      const hasInterestedIn = response.data.interestedIn !== null && response.data.interestedIn !== undefined;

      if (!hasGender || !hasInterestedIn) {
        setShowProfileSetup(true);
      } else {
        setShowProfileSetup(false);
        // Profile is complete - clear any transient navigation state
        if (location.state?.justVerified) {
          navigate(location.pathname, { replace: true, state: {} });
        }
      }
      setProfileCheckComplete(true);
    } catch (err) {
      // Only show setup modal if the error explicitly says profile not found
      const errorMessage = (err.response?.data?.message || err.response?.data?.error || '').toLowerCase();
      const isNotFound = err.response?.status === 404 || errorMessage.includes('not found');
      setShowProfileSetup(isNotFound);
      setProfileCheckComplete(true);
    }
  };

  const loadProfiles = async () => {
    try {
      setLoading(true);
      setError('');
      // Update activity when loading profiles
      profileAPI.updateActivity().catch(() => {});

      const response = await profileAPI.discoverProfiles(0, 20, distanceFilter);
      setProfiles(response.data.content);
      setLoading(false);
    } catch (err) {
      setError(getFriendlyError(err, 'Failed to load profiles. Please try again.'));
      setProfiles([]);
      setLoading(false);
    }
  };

  const handleProfileClick = (userId) => {
    navigate(`/profile/${userId}`);
  };

  const filteredProfiles = profiles.filter(profile => {
    // Search filter
    if (searchQuery && profile.name && !profile.name.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    // Age filter
    if (profile.age && (profile.age < ageFilter.min || profile.age > ageFilter.max)) {
      return false;
    }
    // Gender filter (frontend only - backend already filters by preference)
    if (genderFilter !== 'all' && profile.gender !== genderFilter) {
      return false;
    }
    return true;
  });

  // Categorize profiles into sections
  const now = new Date();
  const threeDaysAgo = new Date(now - 3 * 24 * 60 * 60 * 1000);
  
  // Featured - Top ladies. Prefer promoted/verified, but if there aren't
  // enough, fill the row with the best available (online, has photo) so the
  // Featured section is never empty for a browsing user.
  const hasPhoto = (p) => p.photoUrls && p.photoUrls.length > 0;
  const featuredPriority = filteredProfiles
    .filter(p => p.isPromoted || p.hasVerifiedBadge)
    .sort((a, b) => (b.isPromoted ? 1 : 0) - (a.isPromoted ? 1 : 0));
  const featuredFallback = filteredProfiles
    .filter(p => !p.isPromoted && !p.hasVerifiedBadge && hasPhoto(p))
    .sort((a, b) => {
      // Online first, then more recently active.
      if (a.isOnline !== b.isOnline) return a.isOnline ? -1 : 1;
      return new Date(b.lastActive || 0) - new Date(a.lastActive || 0);
    });
  const featuredProfiles = [...featuredPriority, ...featuredFallback]
    // de-dupe by id, keep order
    .filter((p, i, arr) => arr.findIndex(x => x.id === p.id) === i)
    .slice(0, 6);
  
  // New Members (joined in last 3 days)
  const newMembers = filteredProfiles.filter(p => {
    if (!p.createdAt) return false;
    const createdAt = new Date(p.createdAt);
    return createdAt > threeDaysAgo;
  });
  
  // Online Now
  const onlineProfiles = filteredProfiles.filter(p => p.isOnline);
  
  // Creators - Top 5 (users who are creators)
  const creatorProfiles = filteredProfiles
    .filter(p => p.isCreator)
    .slice(0, 5);
  
  const displayProfiles = filteredProfiles;

  const getLastSeenText = (lastActive, isOnline) => {
    // If user is currently online (backend checks if active within last 2 minutes)
    if (isOnline) return 'Active now';
    
    if (!lastActive) return 'Never active';
    
    const now = new Date();
    // Parse as UTC timestamp - backend sends UTC, add 'Z' if not present
    const lastActiveStr = lastActive.endsWith('Z') ? lastActive : lastActive + 'Z';
    const lastActiveDate = new Date(lastActiveStr);
    const diffMs = now - lastActiveDate;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    // Show minutes for anything under 1 hour
    if (diffMins < 60) return `${diffMins}m ago`;
    
    // Show hours for anything under 24 hours
    if (diffHours < 24) return `${diffHours}h ago`;
    
    // Show "Yesterday" for 1 day ago
    if (diffDays === 1) return 'Yesterday';
    
    // Show days for anything under 7 days
    if (diffDays < 7) return `${diffDays}d ago`;
    
    // Show date for anything older
    return lastActiveDate.toLocaleDateString();
  };

  const getMoodLabel = (mood) => {
    const moods = {
      'CHAT': 'Chat',
      'HOOKUP': 'Hookup',
      'FWB': 'FWB',
      'VIBING': 'Vibing',
    };
    return moods[mood] || 'Social';
  };

  if (loading) {
    return (
      <div className="discover-container">
        <LoadingAnimation userGender={currentUserProfile?.gender} />
      </div>
    );
  }

  return (
    <div className="discover-container">
      {/* Welcome Tutorial for first-time users */}
      {showWelcomeTutorial && (
        <WelcomeTutorial
          onComplete={() => {
            setShowWelcomeTutorial(false);
            const id = tutorialUserId || userId || localStorage.getItem('userId');
            if (id) {
              localStorage.setItem(`hasSeenTutorial_${id}`, 'true');
            }
          }}
          userGender={currentUserProfile?.gender}
        />
      )}

      {/* Profile incomplete: route users to the single canonical setup flow
          instead of duplicating the form here. */}
      {showProfileSetup && profileCheckComplete && (
        <div
          className="modal-overlay"
          style={{ zIndex: 1000 }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="finish-profile-title"
          onClick={() => setShowProfileSetup(false)}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '440px', position: 'relative', textAlign: 'center' }}
          >
            <button
              type="button"
              onClick={() => setShowProfileSetup(false)}
              aria-label="Close"
              style={{
                position: 'absolute', top: '16px', right: '16px',
                background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '50%',
                width: '32px', height: '32px', cursor: 'pointer',
                color: 'var(--text-secondary)', fontSize: '20px', fontWeight: 700
              }}
            >
              ×
            </button>
            <div style={{
              width: '64px', height: '64px', borderRadius: '50%',
              background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 16px', fontSize: '32px', color: 'white'
            }} aria-hidden="true"><HiOutlineUser /></div>
            <h2 id="finish-profile-title" style={{ marginBottom: '8px' }}>Finish your profile</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: '14px', lineHeight: 1.5, marginBottom: '24px' }}>
              Add a few details so we can show you the right people and help others find you.
            </p>
            <button
              onClick={() => navigate('/profile-setup')}
              style={{
                width: '100%', padding: '14px', borderRadius: '8px',
                background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
                border: 'none', color: 'white', fontSize: '15px', fontWeight: 700, cursor: 'pointer'
              }}
            >
              Complete Profile
            </button>
          </div>
        </div>
      )}

      <div className="discover-header">
        <div className="header-content">
          <h1>Browse</h1>
          <p className="discover-subtitle">Discover people near you</p>
        </div>
        
        {/* Activity Banner */}
        <ActivityBanner />
        
        {/* "Available for call" banner removed - video calls are WhatsApp-style from chat */}
        
        {/* Search Bar */}
        <div className="search-filter-bar">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search by name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>
          <button 
            className="filter-toggle-btn"
            onClick={() => setShowFilters(!showFilters)}
          >
            Filters {showFilters ? '▲' : '▼'}
          </button>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="filters-panel">
            <div className="filter-group">
              <label>Maximum Distance: {distanceFilter}km</label>
              <input
                type="range"
                min="5"
                max="500"
                step="5"
                value={distanceFilter}
                onChange={(e) => setDistanceFilter(parseInt(e.target.value))}
                className="distance-slider"
                style={{
                  width: '100%',
                  marginTop: '8px'
                }}
              />
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                fontSize: '12px',
                color: 'var(--text-secondary)',
                marginTop: '4px'
              }}>
                <span>5km</span>
                <span>500km</span>
              </div>
              <button
                onClick={() => loadProfiles()}
                style={{
                  marginTop: '12px',
                  padding: '8px 16px',
                  background: 'var(--primary)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                Apply Distance Filter
              </button>
            </div>
            
            <div className="filter-group">
              <label>Age Range</label>
              <div className="age-range-inputs">
                <input
                  type="number"
                  min="18"
                  max="99"
                  value={ageFilter.min}
                  onChange={(e) => setAgeFilter({ ...ageFilter, min: parseInt(e.target.value) })}
                  className="age-input"
                />
                <span>to</span>
                <input
                  type="number"
                  min="18"
                  max="99"
                  value={ageFilter.max}
                  onChange={(e) => setAgeFilter({ ...ageFilter, max: parseInt(e.target.value) })}
                  className="age-input"
                />
              </div>
            </div>
            
            <div className="filter-group">
              <label>Gender</label>
              <select 
                value={genderFilter} 
                onChange={(e) => setGenderFilter(e.target.value)}
                className="gender-select"
                style={{
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: '1px solid var(--border-color)',
                  background: 'var(--card-bg)',
                  color: 'var(--text-primary)',
                  fontSize: '14px',
                  width: '100%'
                }}
              >
                <option value="all">All Genders</option>
                <option value="MALE">Male</option>
                <option value="FEMALE">Female</option>
                <option value="OTHER">Other</option>
              </select>
              <p style={{ 
                fontSize: '12px', 
                color: 'var(--text-secondary)', 
                marginTop: '6px',
                fontStyle: 'italic' 
              }}>
                Note: Results are filtered by your preference settings
              </p>
            </div>
          </div>
        )}
        
        {/* Filter Tabs */}
        <div className="filter-tabs">
          <button 
            className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All
          </button>
          <button 
            className={`filter-tab ${filter === 'nearby' ? 'active' : ''}`}
            onClick={() => setFilter('nearby')}
          >
            Nearby
          </button>
          <button 
            className={`filter-tab ${filter === 'new' ? 'active' : ''}`}
            onClick={() => setFilter('new')}
          >
            New
          </button>
        </div>
      </div>

      <div className="discover-content">
        <>
            {error ? (
              <div
                role="alert"
                style={{
                  textAlign: 'center',
                  padding: '60px 20px',
                  background: 'var(--surface)',
                  borderRadius: '16px',
                  marginTop: '24px',
                  border: '2px solid rgba(239, 68, 68, 0.3)'
                }}
              >
                <div style={{ fontSize: '64px', marginBottom: '20px', color: '#ef4444', display: 'flex', justifyContent: 'center' }} aria-hidden="true"><HiOutlineExclamationCircle /></div>
                <h3 style={{ marginBottom: '12px', color: 'var(--text-primary)', fontSize: '20px' }}>
                  Couldn't load profiles
                </h3>
                <p style={{ color: 'var(--text-secondary)', marginBottom: '20px', fontSize: '15px', lineHeight: '1.6' }}>
                  {error}
                </p>
                <button
                  onClick={() => loadProfiles()}
                  style={{
                    padding: '12px 28px',
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
                    border: 'none',
                    color: 'white',
                    fontSize: '15px',
                    fontWeight: '700',
                    cursor: 'pointer'
                  }}
                >
                  Try Again
                </button>
              </div>
            ) : displayProfiles.length === 0 ? (
              <EmptyStateDiscover 
                userGender={currentUserProfile?.gender}
                profileCompletion={currentUserProfile?.profileCompletionPercentage || 0}
              />
            ) : (
              <>
                {/* Featured Section - Top 5 */}
                {featuredProfiles.length > 0 && (
                  <div className="profile-section">
                    <div className="section-header">
                      <h2>Featured</h2>
                      <button className="view-all-btn">
                        View All
                      </button>
                    </div>
                    <div className="section-scroll">
                      {featuredProfiles.map((profile) => (
                        <div 
                          key={profile.id} 
                          className="profile-card-section"
                          onClick={() => handleProfileClick(profile.userId)}
                        >
                          <div className="profile-image-section">
                            {profile.photoUrls && profile.photoUrls.length > 0 ? (
                              <img 
                                src={profile.photoUrls[0].replace('/upload/', '/upload/w_500,h_600,c_fill,q_auto,f_auto/')} 
                                alt={profile.name}
                                loading="lazy"
                              />
                            ) : (
                              <div className="placeholder-image-section">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                                  <circle cx="12" cy="7" r="4"/>
                                </svg>
                              </div>
                            )}
                            <div className="popular-badge">POPULAR</div>
                          </div>
                          <div className="profile-info-section">
                            <div className="profile-name-age">
                              <h3>{profile.name || 'Anonymous'}</h3>
                              {profile.age && <span className="profile-age">, {profile.age}</span>}
                            </div>
                            <p className="location-text-section">{profile.location}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* New Members Section */}
                {newMembers.length > 0 && (
                  <div className="profile-section">
                    <div className="section-header">
                      <h2>New Members</h2>
                      <button className="view-all-btn">
                        View All
                      </button>
                    </div>
                    <div className="section-scroll">
                      {newMembers.slice(0, 10).map((profile) => (
                        <div 
                          key={profile.id} 
                          className="profile-card-section"
                          onClick={() => handleProfileClick(profile.userId)}
                        >
                          <div className="profile-image-section">
                            {profile.photoUrls && profile.photoUrls.length > 0 ? (
                              <img 
                                src={profile.photoUrls[0].replace('/upload/', '/upload/w_500,h_600,c_fill,q_auto,f_auto/')} 
                                alt={profile.name}
                                loading="lazy"
                              />
                            ) : (
                              <div className="placeholder-image-section">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                                  <circle cx="12" cy="7" r="4"/>
                                </svg>
                              </div>
                            )}
                            <div className="new-badge">NEW</div>
                          </div>
                          <div className="profile-info-section">
                            <div className="profile-name-age">
                              <h3>{profile.name || 'Anonymous'}</h3>
                              {profile.age && <span className="profile-age">, {profile.age}</span>}
                            </div>
                            <p className="location-text-section">{profile.location}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Online Section */}
                {onlineProfiles.length > 0 && (
                  <div className="profile-section">
                    <div className="section-header">
                      <h2>Online</h2>
                      <button className="view-all-btn">
                        View All
                      </button>
                    </div>
                    <div className="section-scroll">
                      {onlineProfiles.slice(0, 10).map((profile) => (
                        <div 
                          key={profile.id} 
                          className="profile-card-section"
                          onClick={() => handleProfileClick(profile.userId)}
                        >
                          <div className="profile-image-section">
                            {profile.photoUrls && profile.photoUrls.length > 0 ? (
                              <img 
                                src={profile.photoUrls[0].replace('/upload/', '/upload/w_500,h_600,c_fill,q_auto,f_auto/')} 
                                alt={profile.name}
                                loading="lazy"
                              />
                            ) : (
                              <div className="placeholder-image-section">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                                  <circle cx="12" cy="7" r="4"/>
                                </svg>
                              </div>
                            )}
                            <div className="online-badge">ONLINE</div>
                          </div>
                          <div className="profile-info-section">
                            <div className="profile-name-age">
                              <h3>{profile.name || 'Anonymous'}</h3>
                              {profile.age && <span className="profile-age">, {profile.age}</span>}
                            </div>
                            <p className="location-text-section">{profile.location}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Creators Section - Top 5 */}
                {creatorProfiles.length > 0 && (
                  <div className="profile-section">
                    <div className="section-header">
                      <h2>Creators</h2>
                      <button className="view-all-btn">
                        View All
                      </button>
                    </div>
                    <div className="section-scroll">
                      {creatorProfiles.map((profile) => (
                        <div 
                          key={profile.id} 
                          className="profile-card-section"
                          onClick={() => handleProfileClick(profile.userId)}
                        >
                          <div className="profile-image-section">
                            {profile.photoUrls && profile.photoUrls.length > 0 ? (
                              <img 
                                src={profile.photoUrls[0].replace('/upload/', '/upload/w_500,h_600,c_fill,q_auto,f_auto/')} 
                                alt={profile.name}
                                loading="lazy"
                              />
                            ) : (
                              <div className="placeholder-image-section">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                                  <circle cx="12" cy="7" r="4"/>
                                </svg>
                              </div>
                            )}
                            <div className="creator-badge">CREATOR</div>
                          </div>
                          <div className="profile-info-section">
                            <div className="profile-name-age">
                              <h3>{profile.name || 'Anonymous'}</h3>
                              {profile.age && <span className="profile-age">, {profile.age}</span>}
                            </div>
                            <p className="location-text-section">{profile.location}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* All Profiles - everyone is always shown here */}
                {displayProfiles.length > 0 && (
                  <div className="profile-section">
                    <div className="section-header">
                      <h2>All Profiles</h2>
                    </div>
                    <div className="profiles-grid">
                      {displayProfiles.map((profile) => (
                        <div 
                          key={profile.id} 
                          className="profile-card-section"
                          onClick={() => handleProfileClick(profile.userId)}
                        >
                          <div className="profile-image-section">
                            {profile.photoUrls && profile.photoUrls.length > 0 ? (
                              <img 
                                src={profile.photoUrls[0].replace('/upload/', '/upload/w_500,h_600,c_fill,q_auto,f_auto/')} 
                                alt={profile.name}
                                loading="lazy"
                              />
                            ) : (
                              <div className="placeholder-image-section">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                                  <circle cx="12" cy="7" r="4"/>
                                </svg>
                              </div>
                            )}
                          </div>
                          <div className="profile-info-section">
                            <div className="profile-name-age">
                              <h3>{profile.name || 'Anonymous'}</h3>
                              {profile.age && <span className="profile-age">, {profile.age}</span>}
                            </div>
                            <p className="location-text-section">{profile.location}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
        </>
      </div>
    </div>
  );
};

export default Discover;
